import { React, useEffect, useMemo, useState } from 'react';

import { Button } from '@mui/material';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AssignmentTurnedInRoundedIcon from '@mui/icons-material/AssignmentTurnedInRounded';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import FindInPageRoundedIcon from '@mui/icons-material/FindInPageRounded';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { DataGrid } from '@mui/x-data-grid';

import { Alerta } from './alerta';
import DayDatePicker from './dayDatePicker';
import PageHeader from './pageHeader';
import SubmitButton from './submitButton';
import { formatDateLabelPtBr, formatIsoDate } from '../utils/dateUtils';
import { fetchFlask } from '../utils/fetchFlask';
import { maskCpfCnpj } from '../utils/maskCpfCnpj';
import { parseNumberPtBr } from '../utils/numberUtils';
import { formatCurrency } from '../utils/stringFormat';
import './agreementWorkflowPage.css';

const billColumns = [
    {
        field: 'cc',
        headerName: 'CCs',
        width: 150
    },
    {
        field: 'valor',
        headerName: 'Valor',
        width: 150
    },
    {
        field: 'vencimento',
        headerName: 'Vencimento',
        width: 150
    },
    {
        field: 'status',
        headerName: 'Status',
        width: 150
    }
];

const AgreementWorkflowPage = ({
    title,
    formId,
    introTitle,
    introText,
    submitEndpoint,
    submitLabel,
    submitIcon,
    buildPayload,
    renderExtraOptions,
    getSummaryItems,
    summaryTitle = 'Revisão do acordo',
    attentionText,
    completionTotal = 4
}) => {
    const [documento, setDocumento] = useState('');
    const [dateTime, setDateTime] = useState(null);
    const [parcelas, setParcelas] = useState(1);
    const [entrada, setEntrada] = useState(0);
    const [error, setError] = useState(false);
    const [message, setMessage] = useState('');
    const [status, setStatus] = useState('');
    const [openAlerta, setOpenAlerta] = useState(false);
    const [isSubmit, setIsSubmit] = useState(false);
    const [rows, setRows] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);

    const selectedBillsCount = selectedRows.length;
    const selectedBillsTotal = useMemo(() => (
        rows
            .filter((row) => selectedRows.includes(row.id))
            .reduce((total, row) => total + parseNumberPtBr(row.valor), 0)
    ), [rows, selectedRows]);
    const selectedBillsTotalLabel = selectedBillsTotal > 0
        ? formatCurrency(selectedBillsTotal)
        : 'Aguardando seleção';
    const dueDateLabel = formatDateLabelPtBr(dateTime, 'Não definida');
    const completedFields = [
        documento,
        selectedBillsCount > 0,
        parcelas,
        dateTime
    ].filter(Boolean).length;

    useEffect(() => {
        setSelectedRows(rows.map(row => row.id));
    }, [rows]);

    const showResponseAlert = (response) => {
        setMessage(response.message);
        setStatus(response.status);
        setOpenAlerta(true);
    };

    const fetchCCs = async () => {
        if (!documento) {
            return;
        }

        const body = JSON.stringify({ documento });
        const response = await fetchFlask('getBillssAgreement', body);

        if (response.bills) {
            const newRows = response.bills.map((bill, index) => ({
                id: index,
                ...bill
            }));
            setRows(newRows);
            setSelectedRows(newRows.map(row => row.id));
        } else {
            showResponseAlert({
                message: response.message || 'Erro ao buscar CCs',
                status: 'error'
            });
        }
    };

    const resetCommonFields = () => {
        setDocumento('');
        setDateTime(null);
        setParcelas(0);
        setEntrada(0);
    };

    async function submitAgreement(e) {
        e.preventDefault();
        setIsSubmit(true);

        if (!dateTime) {
            setError(true);
            setIsSubmit(false);
            return;
        }

        setError(false);
        const vencimento = formatIsoDate(dateTime);
        const selectedBills = rows
            .filter((row) => selectedRows.includes(row.id))
            .map((row) => row.codigo);

        const basePayload = {
            documento,
            vencimento,
            parcelas,
            entrada,
            codes: selectedBills
        };
        const body = JSON.stringify(buildPayload ? buildPayload(basePayload) : basePayload);
        const response = await fetchFlask(submitEndpoint, body);

        showResponseAlert(response);
        if (response.status === 'success') {
            resetCommonFields();
        }
        setIsSubmit(false);
    }

    const context = {
        documento,
        setDocumento,
        dateTime,
        setDateTime,
        parcelas,
        setParcelas,
        entrada,
        setEntrada,
        rows,
        selectedRows,
        setSelectedRows,
        selectedBillsCount,
        selectedBillsTotalLabel,
        dueDateLabel
    };

    const summaryItems = getSummaryItems
        ? getSummaryItems(context)
        : [
            { label: 'CPF/CNPJ', value: documento || 'Não informado', empty: !documento },
            {
                label: 'Contas selecionadas',
                value: `${selectedBillsCount} de ${rows.length}`,
                empty: selectedBillsCount === 0
            },
            {
                label: 'Valor selecionado',
                value: selectedBillsTotalLabel,
                empty: selectedBillsCount === 0
            },
            {
                label: 'Parcelamento',
                value: `${parcelas || 0} parcela${Number(parcelas) === 1 ? '' : 's'}`
            },
            { label: 'Primeiro vencimento', value: dueDateLabel, empty: !dateTime }
        ];

    return (
        <Box className='mainBox ui-workflow-page'>
            <Alerta
                texto={message}
                status={status}
                open={openAlerta}
                handleClose={() => setOpenAlerta(false)}
            />
            <PageHeader>{title}</PageHeader>

            <form action='' method='POST' id={formId} onSubmit={submitAgreement} className='ui-workflow-layout'>
                <Box className='ui-workflow-workspace'>
                    <Box className='ui-workflow-intro'>
                        <Box>
                            <h2>{introTitle}</h2>
                        </Box>
                        <p>{introText}</p>
                    </Box>

                    <Box className='ui-workflow-panel'>
                        <Box className='ui-heading'>
                            <FindInPageRoundedIcon />
                            <Box>
                                <span>Etapa 1</span>
                                <strong>Consulta do cliente</strong>
                            </Box>
                        </Box>

                        <Box className='ui-lookup-grid'>
                            <TextField
                                required
                                label='CPF/CNPJ'
                                value={documento}
                                inputProps={{ maxLength: 18 }}
                                onChange={(e) => setDocumento(maskCpfCnpj(e.target.value))}
                            />
                            <Button
                                type='button'
                                variant='contained'
                                onClick={fetchCCs}
                                startIcon={<SearchRoundedIcon />}
                            >
                                Buscar CCs
                            </Button>
                        </Box>
                    </Box>

                    <Box className='ui-workflow-panel agreement-workflow-bills-panel'>
                        <Box className='ui-heading'>
                            <ReceiptLongRoundedIcon />
                            <Box>
                                <span>Etapa 2</span>
                                <strong>Contas para acordo</strong>
                            </Box>
                        </Box>

                        <Box className='agreement-workflow-grid-shell'>
                            <DataGrid
                                rows={rows}
                                columns={billColumns}
                                checkboxSelection
                                onRowSelectionModelChange={(ids) => setSelectedRows(ids)}
                                rowSelectionModel={selectedRows}
                                localeText={{ noRowsLabel: 'Sem Unidades' }}
                                hideFooter
                                autoHeight
                            />
                        </Box>
                    </Box>

                    <Box className='ui-workflow-panel'>
                        <Box className='ui-heading'>
                            <PaymentsRoundedIcon />
                            <Box>
                                <span>Etapa 3</span>
                                <strong>Condições do acordo</strong>
                            </Box>
                        </Box>

                        <Box className='agreement-workflow-form-grid'>
                            <TextField
                                required
                                label='Número de parcelas'
                                type='number'
                                inputProps={{ step: 1, min: 1 }}
                                value={parcelas}
                                onChange={(e) => setParcelas(e.target.value)}
                            />
                            <TextField
                                required
                                label='Entrada'
                                type='number'
                                inputProps={{ step: 1, min: 0 }}
                                value={entrada}
                                onChange={(e) => setEntrada(e.target.value)}
                            />
                            <FormControl className='ui-control'>
                                <DayDatePicker
                                    id='data'
                                    label='Primeiro Vencimento'
                                    minDate={new Date('2024-10-01')}
                                    value={dateTime}
                                    onChange={setDateTime}
                                />
                                {error && (
                                    <Typography variant='caption' className='agreement-workflow-error-text'>
                                        Por favor selecione uma data!
                                    </Typography>
                                )}
                            </FormControl>
                        </Box>

                        {renderExtraOptions?.(context)}
                    </Box>
                </Box>

                <Box className='ui-workflow-summary'>
                    <Box className='ui-heading ui-heading-divided'>
                        <FactCheckRoundedIcon />
                        <Box>
                            <span>Resumo</span>
                            <strong>{summaryTitle}</strong>
                        </Box>
                    </Box>

                    <Box className='ui-workflow-summary-list'>
                        {summaryItems.map((item) => (
                            <Box key={item.label}>
                                <span>{item.label}</span>
                                <strong className={item.empty ? 'is-empty' : undefined}>{item.value}</strong>
                            </Box>
                        ))}
                    </Box>

                    <Box className='ui-attention'>
                        <WarningAmberRoundedIcon />
                        <p>{attentionText}</p>
                    </Box>

                    <Box className='ui-attention is-ok ui-completion'>
                        <AssignmentTurnedInRoundedIcon />
                        <Box>
                            <span>Campos críticos</span>
                            <strong>{completedFields}/{completionTotal} preenchidos</strong>
                        </Box>
                    </Box>

                    <SubmitButton disabled={selectedRows.length === 0} isSubmit={isSubmit} startIcon={submitIcon}>
                        {submitLabel}
                    </SubmitButton>
                </Box>
            </form>
        </Box>
    );
};

export default AgreementWorkflowPage;
